import api from "./apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";
import { isSponsorCroUserRecord } from "../constants/userTypeLabels";

const staffCache = {
  organizationId: null,
  data: null,
  promise: null,
};

const studyCache = {
  organizationId: null,
  data: null,
  promise: null,
};

const siteCache = {
  organizationId: null,
  data: null,
  promise: null,
};

const roleCache = {
  organizationId: null,
  data: null,
  promise: null,
};

const normalizeLookupResponse = (response) => {
  const d = response?.data;
  if (Array.isArray(d?.SuccessStory)) return d.SuccessStory;
  if (Array.isArray(d)) return d;
  return [];
};

export const isTruthyFlag = (value) =>
  value === true || value === 1 || value === "1";

export const isActiveStaff = (staff) => {
  if (isTruthyFlag(staff?.InActive)) return false;
  if (isTruthyFlag(staff?.Archive)) return false;

  const status = String(staff?.Status || "")
    .trim()
    .toLowerCase();
  if (status === "inactive") return false;
  if (status === "active") return true;

  const accountStatus = staff?.AccountStatus;
  if (accountStatus === 0 || accountStatus === "0") return true;
  if (accountStatus === 1 || accountStatus === "1") return false;

  const accountStatusText = String(accountStatus || "")
    .trim()
    .toLowerCase();
  if (accountStatusText === "active") return true;
  if (accountStatusText === "inactive" || accountStatusText === "blocked") {
    return false;
  }

  // Same default as user list: not inactive/archived means usable
  return true;
};

export const isInternalStaff = (staff) => {
  if (isSponsorCroUserRecord(staff?.IsCRO) || isSponsorCroUserRecord(staff?.IsCro)) {
    return false;
  }
  return true;
};

export const isExternalStaff = (staff) => !isInternalStaff(staff);

/** Staff eligible for task/assignment pickers (IsCRO = 0 / internal only). */
export const filterAssignableStaff = (staffList = []) =>
  filterStaffList(staffList, { internalOnly: true });

export const isSystemAdministrator = (staff) =>
  String(staff?.RoleName || staff?.Roles || "")
    .trim()
    .toLowerCase() === "system administrator";

const staffMappedToSite = (staff, siteId) => {
  if (!siteId) return false;

  const normalizedSiteId = String(siteId).trim().toLowerCase();
  const mappedSiteIds = String(staff?.MappedSiteIds || "")
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

  return mappedSiteIds.includes(normalizedSiteId);
};

export const staffMappedToAnySite = (staff, siteIds = []) => {
  if (!Array.isArray(siteIds) || siteIds.length === 0) return true;

  const mappedSiteIds = String(staff?.MappedSiteIds || "")
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  const normalizedSiteIds = siteIds
    .map((siteId) => String(siteId).trim().toLowerCase())
    .filter(Boolean);

  return normalizedSiteIds.some((siteId) => mappedSiteIds.includes(siteId));
};

export const filterStaffList = (staffList = [], filters = {}) => {
  const {
    activeOnly = true,
    internalOnly = false,
    externalOnly = false,
    excludeSiteId = null,
    includeSiteIds = null,
    excludeStaffIds = [],
    excludeSystemAdmin = false,
    search = "",
  } = filters;

  const excludedIds = new Set(
    (excludeStaffIds || []).map((id) => String(id).trim().toLowerCase()),
  );
  const searchTerm = String(search || "")
    .trim()
    .toLowerCase();

  return (staffList || []).filter((staff) => {
    if (activeOnly && !isActiveStaff(staff)) return false;
    if (internalOnly && !isInternalStaff(staff)) return false;
    if (externalOnly && !isExternalStaff(staff)) return false;
    if (excludeSystemAdmin && isSystemAdministrator(staff)) return false;

    const staffId = String(staff?.StaffId || "").trim().toLowerCase();
    if (staffId && excludedIds.has(staffId)) return false;

    if (excludeSiteId && staffMappedToSite(staff, excludeSiteId)) return false;

    if (
      Array.isArray(includeSiteIds) &&
      includeSiteIds.length > 0 &&
      !staffMappedToAnySite(staff, includeSiteIds)
    ) {
      return false;
    }

    if (searchTerm) {
      const haystack = [
        staff?.StaffName,
        staff?.FirstName,
        staff?.LastName,
        staff?.EmailId,
        staff?.EmployeeNum,
        staff?.RoleName,
        staff?.PrimarySiteName,
        staff?.MappedSiteNames,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!haystack.includes(searchTerm)) return false;
    }

    return true;
  });
};

export const staffToDropdownOptions = (staffList = []) =>
  (staffList || []).map((staff) => ({
    value: staff.StaffId,
    label: [staff.StaffName, staff.EmployeeNum ? `(${staff.EmployeeNum})` : ""]
      .filter(Boolean)
      .join(" "),
    staff,
  }));

export const clearStaffLookupCache = () => {
  staffCache.organizationId = null;
  staffCache.data = null;
  staffCache.promise = null;
};

export const isActiveStudy = (study) =>
  !isTruthyFlag(study?.InActive) &&
  !isTruthyFlag(study?.Archive) &&
  String(study?.StudyStatus || "")
    .trim()
    .toLowerCase() === "live";

export const dedupeStudiesById = (studyList = []) => {
  const seen = new Map();

  (studyList || []).forEach((study) => {
    const studyId = String(
      study?.StudyId ||
        study?.studyId ||
        study?.StudyID ||
        study?.StudyIdRef ||
        "",
    )
      .trim()
      .toLowerCase();
    if (!studyId || seen.has(studyId)) return;
    seen.set(studyId, {
      ...study,
      StudyId: study?.StudyId || study?.studyId || study?.StudyID || study?.StudyIdRef,
    });
  });

  return [...seen.values()];
};

export const filterStudyList = (studyList = [], filters = {}) => {
  const {
    activeOnly = true,
    excludeStudyIds = [],
    search = "",
  } = filters;

  const excludedIds = new Set(
    (excludeStudyIds || []).map((id) => String(id).trim().toLowerCase()),
  );
  const searchTerm = String(search || "")
    .trim()
    .toLowerCase();

  return (studyList || []).filter((study) => {
    if (activeOnly && !isActiveStudy(study)) return false;

    const studyId = String(study?.StudyId || "").trim().toLowerCase();
    if (studyId && excludedIds.has(studyId)) return false;

    if (searchTerm) {
      const haystack = [
        study?.StudyName,
        study?.StudyNumber,
        study?.StudyNumberFormatted,
        study?.StudyStatus,
        study?.PIName,
        study?.SponsorName,
        study?.ScheduleStatus,
        study?.StudyScheduleId,
        study?.StudySchVersion,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!haystack.includes(searchTerm)) return false;
    }

    return true;
  });
};

export const clearStudyLookupCache = () => {
  studyCache.organizationId = null;
  studyCache.data = null;
  studyCache.promise = null;
};

export const isActiveSite = (site) => isTruthyFlag(site?.IsActive);

export const filterSiteList = (siteList = [], filters = {}) => {
  const { activeOnly = true, search = "" } = filters;
  const searchTerm = String(search || "")
    .trim()
    .toLowerCase();

  return (siteList || []).filter((site) => {
    if (activeOnly && !isActiveSite(site)) return false;

    if (searchTerm) {
      const haystack = [site?.SiteName, site?.SiteAbbr, site?.Location]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(searchTerm)) return false;
    }

    return true;
  });
};

export const siteToDropdownOptions = (siteList = []) =>
  (siteList || []).map((site) => {
    const name = site?.SiteName || "";
    const abbr = site?.SiteAbbr || "";
    return {
      value: site?.SiteId,
      label: abbr ? `${name} (${abbr})` : name,
      site,
    };
  });

export const clearSiteLookupCache = () => {
  siteCache.organizationId = null;
  siteCache.data = null;
  siteCache.promise = null;
};

export const MILESTONE_EXCLUDED_NOTIFICATION_ROLES = ["Super Admin"];

const ROLE_GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isRoleGuid = (value) =>
  ROLE_GUID_RE.test(String(value || "").trim());

export const normalizeRoleId = (value) =>
  String(value || "").trim().toLowerCase();

const roleOptionId = (role) =>
  String(role?.roleId ?? role?.RoleId ?? role?.value ?? "").trim();

const roleOptionName = (role) =>
  String(role?.roleName ?? role?.RoleName ?? role?.Roles ?? role?.label ?? "").trim();

export const findRoleOption = (roleId, roleOptions = []) => {
  const needle = normalizeRoleId(roleId);
  if (!needle) return null;
  return (
    (roleOptions || []).find((role) => normalizeRoleId(roleOptionId(role)) === needle) ||
    null
  );
};

export const filterMilestoneNotificationRoleOptions = (roles = []) =>
  (roles || []).filter(
    (role) =>
      !MILESTONE_EXCLUDED_NOTIFICATION_ROLES.some(
        (excluded) =>
          excluded.toLowerCase() ===
          String(role?.roleName || role?.RoleName || role || "")
            .trim()
            .toLowerCase(),
      ),
  );

/** @deprecated use filterMilestoneNotificationRoleOptions */
export const filterMilestoneNotificationRoles = (roles = []) =>
  (roles || []).filter(
    (role) =>
      !MILESTONE_EXCLUDED_NOTIFICATION_ROLES.some(
        (excluded) => excluded.toLowerCase() === String(role || "").trim().toLowerCase(),
      ),
  );

export const normalizeRoleLookupRows = (rows = []) =>
  filterMilestoneNotificationRoleOptions(
    (rows || [])
      .map((row) => ({
        roleId: String(row?.RoleId || row?.roleId || "").trim(),
        roleName: String(row?.RoleName || row?.Roles || row?.roleName || "").trim(),
      }))
      .filter((role) => role.roleId && role.roleName),
  );

export const normalizeRoleLookup = (rows = []) =>
  normalizeRoleLookupRows(rows).map((role) => role.roleName);

/** Comma-separated stored value -> roleId[] (maps legacy role names when possible). */
export const resolveRecipientRoleIds = (storedValue, roleOptions = []) => {
  const tokens = String(storedValue || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return tokens
    .map((token) => {
      if (isRoleGuid(token)) {
        return findRoleOption(token, roleOptions)?.roleId || token;
      }
      const match = roleOptions.find(
        (role) =>
          roleOptionName(role).toLowerCase() === token.toLowerCase(),
      );
      return match ? roleOptionId(match) : "";
    })
    .filter(Boolean);
};

export const roleIdToName = (roleId, roleOptions = []) => {
  const match = findRoleOption(roleId, roleOptions);
  const name = match ? roleOptionName(match) : "";
  if (name && !isRoleGuid(name)) return name;

  const raw = String(roleId || "").trim();
  if (raw && !isRoleGuid(raw)) return raw;

  return "";
};

export const clearRoleLookupCache = () => {
  roleCache.organizationId = null;
  roleCache.data = null;
  roleCache.promise = null;
};

export const fetchRoleLookup = async (userData, options = {}) => {
  const { forceRefresh = false } = options;
  const organizationId = userData?.OrganizationId;
  const createdBy = userData?.StaffId;

  if (!organizationId || !createdBy) return [];

  if (
    !forceRefresh &&
    roleCache.organizationId === organizationId &&
    Array.isArray(roleCache.data)
  ) {
    return roleCache.data;
  }

  if (
    !forceRefresh &&
    roleCache.organizationId === organizationId &&
    roleCache.promise
  ) {
    return roleCache.promise;
  }

  roleCache.organizationId = organizationId;
  roleCache.promise = api
    .post(API_ENDPOINTS.LOOKUP.LOOKUP_ACTIVITY, {
      OrganizationId: organizationId,
      CreatedBy: createdBy,
      ActionType: "GetRoleList",
    })
    .then((response) => {
      const list =
        response?.success === 1
          ? normalizeRoleLookupRows(normalizeLookupResponse(response))
          : [];
      roleCache.data = list;
      roleCache.promise = null;
      return list;
    })
    .catch((error) => {
      roleCache.promise = null;
      throw error;
    });

  return roleCache.promise;
};

export const fetchSiteLookup = async (userData, options = {}) => {
  const { forceRefresh = false } = options;
  const organizationId = userData?.OrganizationId;
  const createdBy = userData?.StaffId;

  if (!organizationId || !createdBy) return [];

  if (
    !forceRefresh &&
    siteCache.organizationId === organizationId &&
    Array.isArray(siteCache.data)
  ) {
    return siteCache.data;
  }

  if (
    !forceRefresh &&
    siteCache.organizationId === organizationId &&
    siteCache.promise
  ) {
    return siteCache.promise;
  }

  siteCache.organizationId = organizationId;
  siteCache.promise = api
    .post(API_ENDPOINTS.LOOKUP.LOOKUP_ACTIVITY, {
      OrganizationId: organizationId,
      CreatedBy: createdBy,
      ActionType: "GetSiteList",
    })
    .then((response) => {
      const list =
        response?.success === 1 ? normalizeLookupResponse(response) : [];
      siteCache.data = list;
      siteCache.promise = null;
      return list;
    })
    .catch((error) => {
      siteCache.promise = null;
      throw error;
    });

  return siteCache.promise;
};

export const getSiteLookup = async (userData, filters = {}, options = {}) => {
  const siteList = await fetchSiteLookup(userData, options);
  return filterSiteList(siteList, filters);
};

export const getActiveSiteLookup = async (userData, filters = {}, options = {}) =>
  getSiteLookup(
    userData,
    {
      activeOnly: true,
      ...filters,
    },
    options,
  );

export const fetchStudyLookup = async (userData, options = {}) => {
  const { forceRefresh = false } = options;
  const organizationId = userData?.OrganizationId;
  const createdBy = userData?.StaffId;

  if (!organizationId || !createdBy) return [];

  if (
    !forceRefresh &&
    studyCache.organizationId === organizationId &&
    Array.isArray(studyCache.data)
  ) {
    return studyCache.data;
  }

  if (
    !forceRefresh &&
    studyCache.organizationId === organizationId &&
    studyCache.promise
  ) {
    return studyCache.promise;
  }

  studyCache.organizationId = organizationId;
  studyCache.promise = api
    .post(API_ENDPOINTS.LOOKUP.LOOKUP_ACTIVITY, {
      OrganizationId: organizationId,
      CreatedBy: createdBy,
      ActionType: "GetStudyList",
    })
    .then((response) => {
      const list =
        response?.success === 1 ? normalizeLookupResponse(response) : [];
      studyCache.data = list;
      studyCache.promise = null;
      return list;
    })
    .catch((error) => {
      studyCache.promise = null;
      throw error;
    });

  return studyCache.promise;
};

export const getStudyLookup = async (userData, filters = {}, options = {}) => {
  const studyList = await fetchStudyLookup(userData, options);
  return dedupeStudiesById(filterStudyList(studyList, filters));
};

export const getActiveStudyLookup = async (userData, filters = {}, options = {}) =>
  getStudyLookup(
    userData,
    {
      activeOnly: true,
      ...filters,
    },
    options,
  );

export const fetchStaffLookup = async (userData, options = {}) => {
  const { forceRefresh = false } = options;
  const organizationId = userData?.OrganizationId;
  const createdBy = userData?.StaffId;

  if (!organizationId || !createdBy) return [];

  if (
    !forceRefresh &&
    staffCache.organizationId === organizationId &&
    Array.isArray(staffCache.data)
  ) {
    return staffCache.data;
  }

  if (
    !forceRefresh &&
    staffCache.organizationId === organizationId &&
    staffCache.promise
  ) {
    return staffCache.promise;
  }

  staffCache.organizationId = organizationId;
  staffCache.promise = api
    .post(API_ENDPOINTS.LOOKUP.LOOKUP_ACTIVITY, {
      OrganizationId: organizationId,
      CreatedBy: createdBy,
      ActionType: "GetStaffList",
    })
    .then((response) => {
      const list =
        response?.success === 1 ? normalizeLookupResponse(response) : [];
      staffCache.data = list;
      staffCache.promise = null;
      return list;
    })
    .catch((error) => {
      staffCache.promise = null;
      throw error;
    });

  return staffCache.promise;
};

export const getStaffLookup = async (userData, filters = {}, options = {}) => {
  const staffList = await fetchStaffLookup(userData, options);
  return filterStaffList(staffList, filters);
};

export const getInternalActiveStaffLookup = async (
  userData,
  filters = {},
  options = {},
) =>
  getStaffLookup(
    userData,
    {
      activeOnly: true,
      internalOnly: true,
      excludeSystemAdmin: true,
      ...filters,
    },
    options,
  );

/** Active staff (internal + external). System admins excluded. */
export const getActiveStaffLookup = async (userData, filters = {}, options = {}) =>
  getStaffLookup(
    userData,
    {
      activeOnly: true,
      excludeSystemAdmin: true,
      ...filters,
    },
    options,
  );

/** Active internal staff (IsCRO = 0) for assignment pickers across modules. */
export const getAssignableStaffLookup = async (userData, filters = {}, options = {}) =>
  getStaffLookup(
    userData,
    {
      activeOnly: true,
      internalOnly: true,
      ...filters,
    },
    options,
  );

/** @alias getAssignableStaffLookup */
export const fetchAssignableStaffLookup = getAssignableStaffLookup;

export default {
  fetchStaffLookup,
  getStaffLookup,
  getInternalActiveStaffLookup,
  getActiveStaffLookup,
  getAssignableStaffLookup,
  fetchAssignableStaffLookup,
  filterAssignableStaff,
  filterStaffList,
  staffToDropdownOptions,
  clearStaffLookupCache,
  fetchStudyLookup,
  getStudyLookup,
  getActiveStudyLookup,
  filterStudyList,
  dedupeStudiesById,
  clearStudyLookupCache,
  fetchSiteLookup,
  getSiteLookup,
  getActiveSiteLookup,
  filterSiteList,
  siteToDropdownOptions,
  clearSiteLookupCache,
  isActiveStaff,
  isActiveStudy,
  isActiveSite,
  isInternalStaff,
  isExternalStaff,
  isSystemAdministrator,
};
